/**
 * AdminJotAuditDashboardPage Component
 *
 * Admin version of JotAudit dashboard for a specific organization
 * Reuses all provider dashboard components with organizationId passed through
 */

import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { EncounterAuditsList } from './components/encounters/EncounterAuditsList';
import { JotAuditPageLayout } from './components/layout';
import { ProvidersList } from './components/providers/ProvidersList';
import { RulesTabContent } from './components/rules/RulesTabContent';
import { TAB_CONFIG } from './constants';
import type { JotAuditTabType } from './types';

const DEFAULT_TAB: JotAuditTabType = 'encounters';

interface TabPanelProps {
  children?: React.ReactNode;
  value: JotAuditTabType;
  activeTab: JotAuditTabType;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeTab }) => {
  if (value !== activeTab) {
    return null;
  }

  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

export const AdminJotAuditDashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current tab from URL or default
  const currentTab = useMemo<JotAuditTabType>(() => {
    const tab = searchParams.get('tab');
    if (tab && ['encounters', 'providers', 'notes', 'rules', 'claims'].includes(tab)) {
      return tab as JotAuditTabType;
    }
    return DEFAULT_TAB;
  }, [searchParams]);

  // Handle tab change
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: JotAuditTabType) => {
      setSearchParams({ tab: newValue });
    },
    [setSearchParams],
  );

  // Back to the org's admin detail page (the embedded JotAudit lives under
  // Configuration → JotAudit there). The standalone /admin-v2/jotaudit picker
  // page was retired.
  const handleBack = useCallback(() => {
    if (organizationId) {
      navigate(`/admin-v2/organization/${organizationId}`);
    } else {
      navigate('/admin-v2');
    }
  }, [navigate, organizationId]);

  if (!organizationId) {
    return (
      <JotAuditPageLayout maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Organization ID required
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Please select an organization from the list.
          </Typography>
        </Paper>
      </JotAuditPageLayout>
    );
  }

  return (
    <JotAuditPageLayout>
      {/* Page Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ color: theme.palette.text.secondary }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              Audit Center
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              Organization: {organizationId}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              py: 2,
              fontSize: '0.9375rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab value={TAB_CONFIG.encounters.value} label={TAB_CONFIG.encounters.label} />
          <Tab value={TAB_CONFIG.rules.value} label={TAB_CONFIG.rules.label} />
          <Tab value={TAB_CONFIG.providers.value} label={TAB_CONFIG.providers.label} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value="encounters" activeTab={currentTab}>
        <EncounterAuditsList 
          organizationId={organizationId} 
          filterByUserId={searchParams.get('provider') || undefined}
        />
      </TabPanel>

      <TabPanel value="rules" activeTab={currentTab}>
        <RulesTabContent organizationId={organizationId} />
      </TabPanel>

      <TabPanel value="providers" activeTab={currentTab}>
        <ProvidersList organizationId={organizationId} />
      </TabPanel>
    </JotAuditPageLayout>
  );
};

export default AdminJotAuditDashboardPage;

